<template>
  <div class="pb-5">
    <button class="ignore-btn" @click="$router.push('/')">{{ $t('dealEntry.ignore') }}</button>
    <div class="title mb-3">{{ $t('dealEntry.title', {board: boardNumber}) }}</div>


    <!-- Player selector tabs + Camera icon -->
    <div class="player-selector" style="display: flex; align-items: center; gap: 2vw; justify-content: center;">
      <select-with-buttons :options="playerOptions" :selected="currentPlayer"
                           :row-size="4" @click="currentPlayer = $event"></select-with-buttons>
      <div v-if="!southRevealed" class="camera-upload-wrap" style="margin: 0;">
        <b-icon icon="camera" class="camera-upload-icon camera-upload-icon-small" @click="triggerPhotoUpload" />
        <input ref="photoInput" type="file" accept="image/*" capture="environment" style="display:none" @change="onPhotoSelected" />
      </div>
    </div>

    <!-- Current player hand display -->
    <div class="hand-display mb-2">
      <div v-for="suit in suits" :key="suit.index" class="hand-suit-row">
        <span v-html="suit.html" class="suit-symbol"></span>
        <div class="hand-cards-wrap">
          <span v-for="card in getPlayerCardsForSuit(currentPlayer, suit.index)" :key="card"
                class="hand-card" @click="removeCard(currentPlayer, suit.index, card)">
            {{ card }}
          </span>
          <span v-if="getPlayerCardsForSuit(currentPlayer, suit.index).length === 0" class="empty-suit">—</span>
        </div>
      </div>
    </div>

    <!-- Suit selector -->
    <select-with-buttons :options="suitOptions" :selected="selectedSuit"
                         @click="selectedSuit = $event"></select-with-buttons>

    <!-- Rank selector -->
    <select-rank v-if="selectedSuit !== null" :disabled-ranks="disabledRanksForSelectedSuit"
                 @click="addCard(selectedSuit, $event)"></select-rank>

    <!-- Actions -->
    <div class="actions mt-3">
      <b-button variant="danger" @click="resetCurrentPlayer" class="mx-1 action-btn">
        {{ $t('dealEntry.deletePlayer', {player: currentPlayerFullName}) }}
      </b-button>
      <b-button variant="outline-danger" @click="resetAll" class="mx-1 action-btn">
        {{ $t('dealEntry.deleteAll') }}
      </b-button>
    </div>
    <!-- Icona fotocamera spostata sopra, vedi sopra -->
    <div v-if="southRevealed" class="actions mt-2">
      <b-button variant="success" @click="submitDeal" :disabled="!isComplete" class="mx-1 action-btn">
        &#10004; {{ $t('dealEntry.submitDeal') }}
      </b-button>
    </div>

    <b-overlay :show="isSending" no-wrap>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="envelope" font-scale="3" animation="cylon"></b-icon>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import {suitHtml} from '@/lib/cardSuits';
import {fetchCustomMethod, post} from '@/lib/fetcher';
import SelectWithButtons from '@/components/lib/SelectWithButtons';
import SelectRank from '@/components/bridgeSelection/SelectRank';
import { BIcon } from 'bootstrap-vue';

const RANKS = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'];
const PLAYERS = [
  {key: 'N', label: 'N'},
  {key: 'E', label: 'E'},
  {key: 'S', label: 'S'},
  {key: 'W', label: 'W'}
];
const SUITS = [
  {index: 3, html: suitHtml.spades, name: 'spades'},
  {index: 2, html: suitHtml.hearts, name: 'hearts'},
  {index: 1, html: suitHtml.diams, name: 'diams'},
  {index: 0, html: suitHtml.clubs, name: 'clubs'}
];

export default {
  name: 'DealEntry',
  components: {SelectWithButtons, SelectRank, BIcon},
  props: {
    boardNumber: {type: [String, Number], required: true}
  },
  data() {
    return {
      currentPlayer: 'W',
      selectedSuit: null,
      isSending: false,
      southRevealed: false,
      playerHands: {W: [], N: [], E: [], S: []}
    };
  },
  created() {
    if (this.validBoardNumber === null) {
      this.$router.replace({name: 'sendResult'}).catch(() => null);
    }
  },
  computed: {
    validBoardNumber() {
      const board = Number(this.boardNumber);
      return Number.isInteger(board) && board > 0 && board < 255 ? board : null;
    },
    players() { return PLAYERS; },
    suits() { return SUITS; },
    ranks() { return RANKS; },
    threePlayersComplete() {
      return this.playerHands.W.length === 13 &&
          this.playerHands.N.length === 13 &&
          this.playerHands.E.length === 13;
    },
    playerOptions() {
      const keys = this.southRevealed ? ['W', 'N', 'E', 'S'] : ['W', 'N', 'E'];
      return keys.map(key => {
        const count = this.playerHands[key].length;
        const label = this.$t('directions.' + key);
        return {
          value: key,
          text: label + ' <small>' + count + '/13</small>',
          class: [count === 13 ? 'player-complete' : 'scoreButton']
        };
      });
    },
    suitOptions() {
      return SUITS.map(s => ({value: s.index, text: s.html, class: ['suit']}));
    },
    disabledRanksForSelectedSuit() {
      if (this.selectedSuit === null) return [];
      return this.allAssignedCards
          .filter(c => c.suit === this.selectedSuit)
          .map(c => c.rank);
    },
    allAssignedCards() {
      return [
        ...this.playerHands.W,
        ...this.playerHands.N,
        ...this.playerHands.E,
        ...this.playerHands.S
      ];
    },
    isComplete() {
      return this.playerHands.W.length === 13 &&
          this.playerHands.N.length === 13 &&
          this.playerHands.E.length === 13 &&
          this.playerHands.S.length === 13;
    },
    currentPlayerFullName() {
      const positionIndex = { N: '0', E: '1', S: '2', W: '3' };
      return this.$t('positions.' + positionIndex[this.currentPlayer]);
    }
  },
  watch: {
    threePlayersComplete(val) {
      if (val && !this.southRevealed) {
        // Auto-fill Sud with remaining 13 cards
        const assigned = new Set(
            [...this.playerHands.W, ...this.playerHands.N, ...this.playerHands.E]
                .map(c => c.suit + '-' + c.rank)
        );
        const remaining = [];
        for (const suit of SUITS) {
          for (const rank of RANKS) {
            if (!assigned.has(suit.index + '-' + rank)) {
              remaining.push({suit: suit.index, rank});
            }
          }
        }
        this.playerHands.S = remaining;
        this.southRevealed = true;
        this.currentPlayer = 'S';
      }
    }
  },
  methods: {
    triggerPhotoUpload() {
      this.$refs.photoInput.click();
    },
    async onPhotoSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.$refs.photoInput.value = '';
      this.isSending = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const data = await fetchCustomMethod('/api/dealEntry/detect', 'POST', formData);
        if (!data.Success) {
          this.$bvModal.msgBoxOk(data.Message || this.$t('dealEntry.errorRecognition'), {title: this.$t('ui.error'), okVariant: 'danger', centered: true});
          return;
        }
        const suitLetterToIndex = {S: 3, H: 2, D: 1, C: 0};
        const alreadyAssigned = new Set(this.allAssignedCards.map(c => c.suit + '-' + c.rank));
        const hand = this.playerHands[this.currentPlayer];
        for (const entry of (data.DetectedCards || [])) {
          if (hand.length >= 13) break;
          const m = entry.match(/^(10|[AKQJ2-9])([SHDC])$/);
          if (!m) continue;
          const rank = m[1];
          const suit = suitLetterToIndex[m[2]];
          const key = suit + '-' + rank;
          if (alreadyAssigned.has(key)) continue;
          hand.push({suit, rank});
          alreadyAssigned.add(key);
        }
        this.playerHands = {...this.playerHands};
      } catch (err) {
        this.$bvModal.msgBoxOk(this.$t('dealEntry.errorPhoto'), {title: this.$t('ui.error'), okVariant: 'danger', centered: true});
      } finally {
        this.isSending = false;
      }
    },
    getPlayerCardsForSuit(player, suitIndex) {
      return (this.playerHands[player] || [])
          .filter(c => c.suit === suitIndex)
          .map(c => c.rank)
          .sort((a, b) => RANKS.indexOf(a) - RANKS.indexOf(b));
    },
    isCardTaken(suitIndex, rank) {
      return this.allAssignedCards.some(c => c.suit === suitIndex && c.rank === rank);
    },
    addCard(suitIndex, rank) {
      if (this.playerHands[this.currentPlayer].length >= 13) return;
      this.playerHands[this.currentPlayer].push({suit: suitIndex, rank});
      // Auto-advance solo nella fase W/N/E
      if (!this.southRevealed && this.playerHands[this.currentPlayer].length === 13) {
        const nextIncomplete = ['W', 'N', 'E'].find(p => this.playerHands[p].length < 13);
        if (nextIncomplete) this.currentPlayer = nextIncomplete;
        this.selectedSuit = null;
      } else if (this.playerHands[this.currentPlayer].length === 13) {
        this.selectedSuit = null;
      }
    },
    removeCard(player, suitIndex, rank) {
      const idx = this.playerHands[player].findIndex(c => c.suit === suitIndex && c.rank === rank);
      if (idx !== -1) this.playerHands[player].splice(idx, 1);
    },
    async resetCurrentPlayer() {
      const ok = await this.$bvModal.msgBoxConfirm(
          this.$t('dealEntry.confirmDeletePlayer', {player: this.currentPlayerFullName}),
          {title: this.$t('dealEntry.titleDeletePlayer'), okVariant: 'danger', okTitle: this.$t('dealEntry.deleteBtn'), cancelTitle: this.$t('ui.cancel'), centered: true}
      );
      if (!ok) return;
      this.playerHands[this.currentPlayer] = [];
    },
    async resetAll() {
      const ok = await this.$bvModal.msgBoxConfirm(
          this.$t('dealEntry.confirmDeleteAll'),
          {title: this.$t('dealEntry.titleDeleteAll'), okVariant: 'danger', okTitle: this.$t('dealEntry.deleteAllBtn'), cancelTitle: this.$t('ui.cancel'), centered: true}
      );
      if (!ok) return;
      this.playerHands = {W: [], N: [], E: [], S: []};
      this.southRevealed = false;
      this.currentPlayer = 'W';
      this.selectedSuit = null;
    },
    toG2bData(hands) {
      const rankMap = {'A':0,'K':1,'Q':2,'J':3,'10':4,'9':5,'8':6,'7':7,'6':8,'5':9,'4':10,'3':11,'2':12};
      // frontend suit index: 3=spades, 2=hearts, 1=diamonds, 0=clubs
      // g2b suit: 0=spades, 1=hearts, 2=diamonds, 3=clubs  →  g2bSuit = 3 - frontendSuit
      const toCardValue = (card) => (3 - card.suit) * 13 + rankMap[card.rank];
      const order = ['N', 'E', 'S', 'W'];
      const data = [];
      order.forEach(player => hands[player].forEach(card => data.push(toCardValue(card))));
      return data.join(' ');
    },
    async submitDeal() {
      if (!this.isComplete || this.validBoardNumber === null) return;
      const ok = await this.$bvModal.msgBoxConfirm(
          this.$t('dealEntry.confirmSubmit'),
          {title: this.$t('dealEntry.titleSubmit'), okVariant: 'success', okTitle: this.$t('dealEntry.sendBtn'), cancelTitle: this.$t('ui.cancel'), centered: true}
      );
      if (!ok) return;
      const currentRound = this.$store.getters['tournament/currentRound'] || 1;
      const currentSession = this.$store.getters['tournament/currentSession'] || 1;
      const payload = {
        series: 1,
        group: 1,
        session: currentSession,
        round: currentRound,
        number: this.validBoardNumber,
        data: this.toG2bData(this.playerHands)
      };
      this.isSending = true;
      post('/api/dealEntry', payload)
          .then(async () => { await this.$bvModal.msgBoxOk(this.$t('dealEntry.submitSuccess'), {title: this.$t('ui.success'), okVariant: 'success', centered: true}); this.$router.push({name: 'sendResult'}); })
          .catch(() => { this.$bvModal.msgBoxOk(this.$t('dealEntry.errorSend'), {title: this.$t('ui.error'), okVariant: 'danger', centered: true}); })
          .finally(() => { this.isSending = false; });
    }
  }
};
</script>

<style scoped>
 .camera-upload-wrap {
   display: flex;
   justify-content: center;
   align-items: center;
   margin: 2vw 0 3vw 0;
 }
  .camera-upload-icon {
    cursor: pointer;
    color: #1e3a5f;
    transition: color 0.2s, transform 0.2s;
  }
  /* Dimensione icona fotocamera uguale ai pulsanti giocatore */
  .camera-upload-icon-small {
    font-size: 4vw;
    height: 12vw;
    width: 12vw;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 1vw;
  }
* {
  text-align: center;
}

.title {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e3a5f;
  letter-spacing: -0.01em;
}

.hand-display {
  background: #f8f9fa;
  border-radius: 0.5rem;
  padding: 2vw;
  margin: 0 2vw;
}

.hand-suit-row {
  display: flex;
  align-items: center;
  gap: 1.5vw;
  padding: 1vw 0;
  font-size: 6vw;
}

.hand-cards-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5vw;
  flex: 1;
}

.suit-symbol {
  width: 10vw;
  font-size: 7vw;
  flex-shrink: 0;
}

.hand-card {
  background: #e9ecef;
  border-radius: 0.4rem;
  padding: 1vw 3vw;
  cursor: pointer;
  font-weight: bold;
  font-size: 5.5vw;
}

.hand-card:hover {
  background: #ffcccb;
}


.empty-suit {
  color: #ccc;
}


.actions {
  display: flex;
  justify-content: center;
}

.action-btn {
  font-size: 4vw;
}

>>> .scoreButton {
  background: #fff;
  border: 1.5px solid #1e3a5f;
  color: #1e3a5f;
  font-weight: 600;
}

.player-selector >>> .square {
  height: 15vw;
  font-size: 8vw;
  line-height: 1.2;
  flex-direction: column;
  justify-content: center;
}

.player-selector >>> .square small {
  font-size: 4.5vw;
}

.player-selector >>> .selected {
  background-color: #1e3a5f;
  color: white;
  border-color: #1e3a5f;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.3);
}

>>> .player-complete {
  background: #d4edda;
  border: 1px solid #28a745;
}

>>> .suit span {
  line-height: 2.3rem;
}

.ignore-btn {
  position: fixed;
  top: 4vw;
  right: 4vw;
  z-index: 1000;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 2rem;
  padding: 2vw 5vw;
  font-size: 4vw;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0,0,0,0.25);
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.ignore-btn:hover {
  background: #c0392b;
  transform: scale(1.05);
}
</style>
